import {
  Alert,
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Chip } from "@mui/material";
import AccountVerificationChip from "./AccountVerificationChip";
import AccountVerificationAuditLogExpander from "./AccountVerificationAuditLogExpander";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import InputField from "components/Input/InputField";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaFileInvoice, FaPlusCircle } from "react-icons/fa";
import {
  CopyAllOutlined,
  DeleteForeverOutlined,
  DeleteOutlined,
  InfoOutlined,
  MoreVert,
  NotificationsActiveOutlined,
  NoteOutlined,
  OpenInNew,
  Send,
  StickyNote2Outlined,
  SwapHoriz,
  TaskAltOutlined,
} from "@mui/icons-material";
import { SummarizeOutlined } from "@mui/icons-material";
import ForceDeleteTermDialog from "./ForceDeleteTermDialog";
import SendNotificationModal from "./SendNotificationModal";
import CompletedInstallmentsSummary from "./CompletedInstallmentsSummary";
import TermNotificationsCell from "./TermNotificationsCell";
import NotesModal from "./NotesModal";
import DiscussionNotesModal from "components/Modals/NotesModal";
import {
  admissionTermNumbersShown,
  createNewAdmissionDetail,
  calculateRowValues,
  calculateSubRowValues,
  chipOnlyFieldsFromTerm,
  isInstallmentBonusClaimed,
  isInstallmentCommissionClaimed,
  toNumber,
  formatDateForInput,
  shouldShowAllAdmissionTerms,
  getSwappableImportedTermNumbers,
  scopePaymentVerificationsToCad,
  scopeTermVerificationsToEntry,
} from "./accountUtils";
import { formatMoney } from "utils/utils";
import axios from "axios";
import { useAdmissionTerms } from "../Admissions/AddAdmissions/admissionApi";
import {
  checkAndImportAdmissionTerm,
  importAdmissionTermForProvider,
  setAdmissionTermPreImportAmounts,
} from "./accountsApi";
import TermSwapDialog from "./TermSwapDialog";
import { canRequestCreditAdjustment, hasPermission, isSuperAdmin } from "utils/permissionUtils";
import { P } from "const/permissions";
import {
  hasPendingVerificationPayment,
  netPendingPaidFromTermPayments,
  netVerifiedPaidFromTermPayments,
  paymentDraftsFromBackend,
  resolveTermPaidAmountFromPayments,
} from "../Admissions/AddAdmissions/applicationFlow/termPaymentUtils";
import TermPaymentsModal from "../Admissions/AddAdmissions/applicationFlow/TermPaymentsModal";
import CreditAdjustmentDialog from "../Admissions/AddAdmissions/applicationFlow/CreditAdjustmentDialog";
import { termCreditAvailable } from "../Admissions/AddAdmissions/applicationFlow/creditAdjustmentUtils";
import { API_URL } from "const/constants";
import { fetchAccountTermNotificationCount } from "services/accountTermNotificationsApi";
import { toast } from "react-toastify";
import useKothar from "context/useKothar";
import { useNavigate } from "react-router-dom";
import { ensureProviderAccount, findProviderByUniversityId } from "./providerUtils";
import AccountVerificationModal from "./verification/AccountVerificationModal";
import {
  usePendingPaymentVerifications,
  usePendingTermVerifications,
} from "./verification/verificationTaskApi";

const parseTermNumber = (value, fallback) => {
  const raw = value ?? fallback;
  const parsed = Number(String(raw).trim().replace(/^T/i, ""));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const LOCKED_VERIFICATION_STATUSES = new Set(["PENDING", "VERIFIED", "IMPORTED"]);
// IMPORTED is fully verified — sending to provider is allowed
const PROVIDER_SEND_LOCKED_STATUSES = new Set(["PENDING", "VERIFIED"]);

/**
 * A withdrawn term keeps its scheduled commission/bonus in the field — the amount is still the
 * amount — but the withdrawal decides whether the agency keeps it. Unretained money is excluded
 * from profit/loss and agent cost, so say so here rather than leaving a figure that looks counted.
 */
const WrittenOffBadge = ({ retained, withdrawalState, what }) =>
  withdrawalState && retained !== true ? (
    <Tooltip
      title={`Withdrawn without retaining this ${what} — it does not count toward profit.`}
      arrow
    >
      <span className="shrink-0 rounded border border-app-border px-1 py-0.5 text-[10px] uppercase tracking-wide text-app-text-muted">
        written off
      </span>
    </Tooltip>
  ) : null;

const AddAccountSecondTable = ({
  parentId,
  accountDetails,
  setAccountDetails,
  data,
  clientData,
  onDirty,
  onLocalNoteAdded,
  clientAccountDetailId,
  onVerifySuccess,
  openVerificationOnMount = false,
  autoCloseVerificationWhenEmpty = false,
  onVerificationModalOpened,
  openTermNotesOnMount = null,
  onTermNotesOpened,
  summaryMode = false,
  onRequestFullView,
  onBackToSummary,
  /** Opens the withdrawal settlement for this admission — the accounts team's half of a withdrawal. */
  onAdjustSettlement,
}) => {
  const [openNotesModal, setOpenNotesModal] = useState({
    state: false,
    id: null,
    data: null,
    parentId: parentId,
  });
  const [openTermNotesModal, setOpenTermNotesModal] = useState({ state: false, termIndex: -1 });
  const [accountTermNotesModal, setAccountTermNotesModal] = useState({ open: false, row: null });
  // Account notes carry payment/term-swap/internal financial detail, so viewing them needs the
  // Accounts permission. Term notes are generic verification discussion and stay open to everyone.
  const canViewAccountNotes = hasPermission(P.ACCOUNTS.NOTE_READ);
  const [termNotesCounts, setTermNotesCounts] = useState({});
  const [termNotificationCounts, setTermNotificationCounts] = useState({});
  const [rejectDialogId, setRejectDialogId] = useState(null);
  const [rejectFeedback, setRejectFeedback] = useState("");
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [termSwapOpen, setTermSwapOpen] = useState(false);
  // Super-admin force-delete of a term (even with payments): the target term number, or null.
  const [forceDeleteTerm, setForceDeleteTerm] = useState(null);
  // Manual (invoice-free) commission/bonus claim dialog. Shape:
  // { rowData, termNumber, component: 'commission'|'bonus', targetClaimed, note, mode }
  const [claimDialog, setClaimDialog] = useState(null);
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [providerSendDialog, setProviderSendDialog] = useState(null);
  const [providerSendBusy, setProviderSendBusy] = useState(null);
  const superAdmin = isSuperAdmin();
  const [{ providerList, usersList }, { refetchProvider, refetchUsers }] = useKothar();
  const [notifyModal, setNotifyModal] = useState({ open: false, row: null, rowIndex: null });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!usersList?.length) refetchUsers();
  }, [usersList?.length, refetchUsers]);

  const userDetail = JSON.parse(localStorage.getItem("userDetail") || "null");
  const roleValue = userDetail?.type ?? userDetail?.role;
  const userRole = typeof roleValue === "string" ? roleValue : roleValue?.roleType;
  const canVerifyOrRejectVerification = ["SUPER_ADMIN", "ACCOUNT"].includes(
    String(userRole || "").toUpperCase()
  );

  const parentRow = accountDetails.find((item) => item.uuid === parentId);
  const acountId = parentRow?.id;
  const cadId = clientAccountDetailId ?? acountId;
  const isAdmissionModule = parentRow?.module === "Admission";

  useEffect(() => {
    if (!openVerificationOnMount || !isAdmissionModule) return;
    setVerificationModalOpen(true);
    onVerificationModalOpened?.();
  }, [openVerificationOnMount, isAdmissionModule, onVerificationModalOpened]);

  // Deep-link from a term-note mention notification: open the term's notes.
  useEffect(() => {
    if (openTermNotesOnMount == null || !isAdmissionModule) return;
    const termIndex = Number(openTermNotesOnMount) - 1;
    if (termIndex >= 0) {
      setOpenTermNotesModal({ state: true, termIndex });
    }
    onTermNotesOpened?.();
  }, [openTermNotesOnMount, isAdmissionModule, onTermNotesOpened]);

  const { data: admissionTerms = [], dataUpdatedAt: admissionTermsUpdatedAt } = useAdmissionTerms(
    isAdmissionModule ? cadId : null
  );

  const [pendingTermAmounts, setPendingTermAmounts] = useState({});

  const { mutate: savePreImportAmount } = useMutation({
    mutationFn: ({ admissionTermId, field, value }) =>
      setAdmissionTermPreImportAmounts(admissionTermId, { [field]: value }),
    onError: () => toast.error("Failed to save — please try again"),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["admissionTerms", cadId] }),
  });

  const [termPaymentsModal, setTermPaymentsModal] = useState({
    open: false,
    admissionTermId: null,
  });
  const [termPaymentsDraft, setTermPaymentsDraft] = useState([]);
  const [creditMove, setCreditMove] = useState(null);

  const isAdmissionInvoiceLocked = useCallback((rowData = {}) => {
    const status = String(rowData.invoicePaymentStatus || "").toUpperCase();
    return (
      rowData.lockedByPaidProviderInvoice === true ||
      rowData.sentToProvider === true ||
      rowData.hasInvoice === true ||
      status === "PAID" ||
      status === "PARTIALLY_PAID" ||
      toNumber(rowData.paidAmount) > 0
    );
  }, []);
  const getInstallmentData = useCallback(async () => {
    if (!acountId) return [];
    const response = await axios.get(`${API_URL}/admissions/${acountId}/installments`);
    return response?.data?.data;
  }, [acountId]);

  useEffect(() => {
    if (!providerList?.length) refetchProvider?.();
    getInstallmentData();
  }, [getInstallmentData, parentId, providerList?.length, refetchProvider]);

  const admissionIdForTermNotes = parentRow?.admissionValues?.studentDbId;

  const clientId = isAdmissionModule ? clientData?.id : null;
  const pendingPayments = usePendingPaymentVerifications(clientId, !!clientId);
  const pendingTerms = usePendingTermVerifications(clientId, !!clientId);
  const admissionStudentDbId = parentRow?.admissionValues?.studentDbId;
  const pendingVerificationCount =
    scopePaymentVerificationsToCad(pendingPayments.data, acountId).length +
    scopeTermVerificationsToEntry(pendingTerms.data, {
      cadId: acountId,
      studentDbId: admissionStudentDbId,
    }).length;

  const termNumsForCounts = useMemo(
    () => admissionTermNumbersShown(parentRow?.admissionDetails, admissionTerms),
    [parentRow?.admissionDetails, admissionTerms]
  );

  // Verifying a payment or a credit move writes term notes and refetches the schedule, so the
  // counts follow the schedule's refreshes.
  const { data: termNotesCountsData } = useQuery({
    queryKey: [
      "term-notes-counts",
      clientData?.id,
      admissionIdForTermNotes,
      termNumsForCounts.join(","),
      admissionTermsUpdatedAt,
    ],
    queryFn: async () => {
      const entries = await Promise.all(
        termNumsForCounts.map(async (termNum) => {
          const termIndex = termNum - 1;
          const r = await axios.get(
            `${API_URL}/discussion/${clientData.id}?type=admission_term_${admissionIdForTermNotes}_${termIndex}`
          );
          return [termIndex, (r.data?.comments ?? r.data?.data ?? []).length];
        })
      );
      return Object.fromEntries(entries);
    },
    enabled: !!clientData?.id && !!admissionIdForTermNotes && termNumsForCounts.length > 0,
  });

  useEffect(() => {
    if (termNotesCountsData) setTermNotesCounts(termNotesCountsData);
  }, [termNotesCountsData]);

  const { data: termNotificationCountsData } = useQuery({
    queryKey: ["term-notification-counts", cadId, clientData?.id, termNumsForCounts.join(",")],
    queryFn: async () => {
      const entries = await Promise.all(
        termNumsForCounts.map(async (termNumber) => {
          const count = await fetchAccountTermNotificationCount({
            clientAccountDetailId: cadId,
            termNumber,
            clientId: clientData.id,
          });
          return [termNumber, count];
        })
      );
      return Object.fromEntries(entries);
    },
    enabled: !!cadId && !!clientData?.id && isAdmissionModule && termNumsForCounts.length > 0,
  });

  useEffect(() => {
    if (termNotificationCountsData) setTermNotificationCounts(termNotificationCountsData);
  }, [termNotificationCountsData]);

  const { data: verifications = [], refetch: refetchVerifications } = useQuery({
    queryKey: ["account-verification-detail-all", acountId],
    queryFn: () =>
      axios
        .get(`${API_URL}/account-verification/detail/${acountId}/all`)
        .then((r) => r.data?.data ?? []),
    enabled: !!acountId,
    refetchOnWindowFocus: true,
  });

  const handleVerificationActionComplete = useCallback(() => {
    refetchVerifications();
    queryClient.invalidateQueries({ queryKey: ["admissionTerms", cadId] });
    queryClient.invalidateQueries({ queryKey: ["pending-payment-verifications", clientData?.id] });
    queryClient.invalidateQueries({ queryKey: ["pending-term-verifications", clientData?.id] });
    onVerifySuccess?.();
  }, [refetchVerifications, queryClient, clientAccountDetailId, clientData?.id, onVerifySuccess]);

  const verificationByAdmissionDetailId = useMemo(() => {
    const map = new Map();
    (verifications || []).forEach((item) => {
      if (item?.admissionDetailId) map.set(item.admissionDetailId, item);
    });
    return map;
  }, [verifications]);

  const verificationByTerms = useMemo(() => {
    const map = new Map();
    (verifications || []).forEach((item) => {
      if (item?.admissionDetailId) return; // already in primary map
      (item?.submittedAccountDetail?.admissionDetails || []).forEach((d) => {
        if (d?.terms) {
          const key = String(d.terms).trim().toUpperCase().replace(/^T/, "");
          if (!map.has(key)) map.set(key, item);
        }
      });
    });
    return map;
  }, [verifications]);

  const stagedDetailForTerm = useCallback(
    (termNumber) => {
      const key = String(termNumber).trim().replace(/^T/i, "");
      const verification = verificationByTerms.get(key);
      if (!verification?.submittedAccountDetail?.admissionDetails?.length) {
        return null;
      }
      return (
        verification.submittedAccountDetail.admissionDetails.find((detail) => {
          const detailKey = String(detail?.terms ?? "")
            .trim()
            .replace(/^T/i, "");
          return detailKey === key;
        }) ?? null
      );
    },
    [verificationByTerms]
  );

  const mergedData = useMemo(() => {
    if (!isAdmissionModule || admissionTerms.length === 0) return data || [];
    const importedByTermNum = new Map();
    (data || []).forEach((d) => {
      const num = parseTermNumber(d.terms, null);
      if (num != null) importedByTermNum.set(num, d);
    });
    const termClaimFields = (term) => {
      const deliberatelyClaimed = Boolean(term.claimedDate);
      return {
        admissionTermId: term.id,
        admissionTermNumber: term.termNumber,
        payments: term.payments || [],
        // Term-anchored account notes (union of ADMISSION_TERM + legacy ADMISSION_DETAIL) — available
        // before import and kept across import; see NoteService ADMISSION_TERM.
        _termNotes: term.notes || [],
        sentToProvider: Boolean(term.sentToProvider),
        withdrawalState: term.withdrawalState ?? null,
        withdrawalSettlementMode: term.withdrawalSettlementMode ?? null,
        settledAgreed: term.settledAgreed ?? null,
        withdrawalCommissionRetained: term.withdrawalCommissionRetained ?? null,
        withdrawalBonusRetained: term.withdrawalBonusRetained ?? null,
        // N/A means "nothing to claim here" — counts as settled for the completed view without a claim.
        commissionNotApplicable: Boolean(term.commissionNotApplicable),
        bonusNotApplicable: Boolean(term.bonusNotApplicable),
        ...chipOnlyFieldsFromTerm(term),
        ...(deliberatelyClaimed
          ? {
              commissionClaimedDirect: Boolean(term.isCommissionClaimed),
              bonusClaimedDirect: Boolean(term.isBonusClaimed),
              claimNote: term.claimedNote ?? undefined,
            }
          : {}),
      };
    };
    return admissionTerms.map((term) => {
      const imported = importedByTermNum.get(term.termNumber);
      const staged = stagedDetailForTerm(term.termNumber);
      const verifiedPaid = Math.max(0, netVerifiedPaidFromTermPayments(term.payments));
      const pendingPaid = netPendingPaidFromTermPayments(term.payments);
      const resolvedPaidAmount = resolveTermPaidAmountFromPayments(
        term.payments,
        imported?.paidAmount
      );

      if (imported) {
        return calculateSubRowValues({
          ...imported,
          ...termClaimFields(term),
          date: imported.date || imported.admissionDate || term.admissionDate || "",
          censusDate: imported.censusDate ?? term.censusDate ?? "",
        });
      }

      const baseRow = {
        _unimported: true,
        ...termClaimFields(term),
        terms: `T${term.termNumber}`,
        date: staged?.date || staged?.admissionDate || term.admissionDate || "",
        admissionDate: term.admissionDate,
        censusDate: staged?.censusDate ?? term.censusDate ?? "",
        perSemFee: staged?.perSemFee ?? term.perSemFee ?? "",
        materialFee: staged?.materialFee ?? term.materialFee ?? "",
        discount: staged?.discount ?? term.discount ?? "",
        commission: staged?.commission ?? term.commission ?? "",
        bonus: staged?.bonus ?? term.bonus ?? "",
        // No installment yet, so the term's own claim state prices the row.
        isCommissionClaimed: Boolean(term.isCommissionClaimed),
        isBonusClaimed: Boolean(term.isBonusClaimed),
        paidAmount: resolvedPaidAmount,
        _verifiedPaid: verifiedPaid,
        _pendingPaid: pendingPaid,
      };
      return calculateSubRowValues(baseRow);
    });
  }, [isAdmissionModule, admissionTerms, data, stagedDetailForTerm]);

  const settlementRow = useMemo(
    () => mergedData.find((r) => r.withdrawalState === "SETTLEMENT") ?? null,
    [mergedData]
  );

  const termPaymentsModalRow = useMemo(
    () => mergedData.find((r) => r.admissionTermId === termPaymentsModal.admissionTermId) ?? null,
    [mergedData, termPaymentsModal.admissionTermId]
  );

  useEffect(() => {
    if (termPaymentsModal.open && termPaymentsModalRow) {
      setTermPaymentsDraft(paymentDraftsFromBackend(termPaymentsModalRow.payments));
    }
  }, [termPaymentsModal.open, termPaymentsModalRow]);

  const getRowVerification = useCallback(
    (rowData = {}) => {
      if (rowData?.id && verificationByAdmissionDetailId.has(rowData.id)) {
        return verificationByAdmissionDetailId.get(rowData.id);
      }
      if (rowData?.terms) {
        const key = String(rowData.terms).trim().toUpperCase().replace(/^T/, "");
        return verificationByTerms.get(key) ?? null;
      }
      return null;
    },
    [verificationByAdmissionDetailId, verificationByTerms]
  );

  const isVerificationLocked = useCallback(
    (rowData = {}) => LOCKED_VERIFICATION_STATUSES.has(getRowVerification(rowData)?.status),
    [getRowVerification]
  );

  const isProviderSendLocked = useCallback(
    (rowData = {}) => {
      if (rowData._unimported || !rowData?.id) return true;
      return PROVIDER_SEND_LOCKED_STATUSES.has(getRowVerification(rowData)?.status);
    },
    [getRowVerification]
  );

  const isInstallmentEditLocked = useCallback(
    (rowData = {}) => isAdmissionInvoiceLocked(rowData) || isVerificationLocked(rowData),
    [isAdmissionInvoiceLocked, isVerificationLocked]
  );

  const swappableTermNumbers = useMemo(
    () =>
      getSwappableImportedTermNumbers({
        installmentRows: mergedData,
        getVerificationForRow: getRowVerification,
      }),
    [mergedData, getRowVerification]
  );

  const swapPaidInvoiceWarning = useMemo(
    () =>
      mergedData.some(
        (row) =>
          !row._unimported &&
          row.lockedByPaidProviderInvoice === true &&
          swappableTermNumbers.includes(parseTermNumber(row.terms, null))
      ),
    [mergedData, swappableTermNumbers]
  );

  const handleTermSwapSuccess = useCallback(
    (response) => {
      toast.success(response?.message || "Terms swapped successfully");
      if (response?.data?.warnings?.includes("paid_invoice_unchanged")) {
        toast.warning(
          "Paid/partial provider invoice lines were not updated. Review provider invoices manually."
        );
      }
      if (response?.data?.warnings?.includes("custom_invoice_description_review")) {
        toast.warning(
          "Custom invoice descriptions were kept. Review provider invoice lines for outdated term labels."
        );
      }
      refetchVerifications();
      queryClient.invalidateQueries({ queryKey: ["admissionTerms", cadId] });
      queryClient.invalidateQueries({ queryKey: ["account-verification-detail-all", acountId] });
      queryClient.invalidateQueries({
        queryKey: ["pending-payment-verifications", clientData?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["pending-term-verifications", clientData?.id] });
      onVerifySuccess?.();
    },
    [
      acountId,
      clientAccountDetailId,
      clientData?.id,
      onVerifySuccess,
      queryClient,
      refetchVerifications,
    ]
  );

  const installmentLockTitle = useCallback(
    (rowData = {}) => {
      if (isVerificationLocked(rowData)) {
        return "This installment is locked because it is pending, verified, or imported in account verification.";
      }
      if (isAdmissionInvoiceLocked(rowData)) {
        return "This installment is locked because it is paid, invoiced, or already sent to provider.";
      }
      return undefined;
    },
    [isAdmissionInvoiceLocked, isVerificationLocked]
  );

  const lockedInputProps = useCallback(
    (rowData = {}) => ({
      disabled: isInstallmentEditLocked(rowData),
      title: installmentLockTitle(rowData),
    }),
    [installmentLockTitle, isInstallmentEditLocked]
  );

  const admissionFieldLockedInputProps = useCallback(
    (rowData = {}) => {
      if (isAdmissionModule) {
        return {
          disabled: true,
          title: "Admission schedule fields are read-only. Edit from Manage Admissions.",
        };
      }
      return lockedInputProps(rowData);
    },
    [isAdmissionModule, lockedInputProps]
  );

  const verificationLockedInputProps = useCallback(
    (rowData = {}) => ({
      disabled: isVerificationLocked(rowData),
      title: isVerificationLocked(rowData) ? installmentLockTitle(rowData) : undefined,
    }),
    [installmentLockTitle, isVerificationLocked]
  );

  const admissionPaidLockedInputProps = useCallback(
    (rowData = {}) => {
      if (isAdmissionModule) {
        return {
          disabled: true,
          title: "Paid amount is managed through the term payment workflow.",
        };
      }
      return verificationLockedInputProps(rowData);
    },
    [isAdmissionModule, verificationLockedInputProps]
  );

  // Commission/bonus: editable post-import; lock only when claimed or provider invoice paid
  const isCommissionLocked = useCallback((rowData = {}) => {
    // Permanently lock once sent to provider
    if (Boolean(rowData.sentToProvider)) return true;
    // Lock when claimed directly (e.g. legacy migrated claim, no provider invoice)
    if (Boolean(rowData.commissionClaimedDirect)) return true;
    // Lock when commission invoice is paid or partially paid — use per-component lock flag
    if (Boolean(rowData.isCommissionInvoiceLocked)) return true;
    const status = String(rowData.invoicePaymentStatus || "").toUpperCase();
    // Fallback: lock on overall invoice status only when no per-component data
    if (!rowData.hasCommissionInvoice && (status === "PAID" || status === "PARTIALLY_PAID"))
      return true;
    return false;
  }, []);

  const isBonusLocked = useCallback((rowData = {}) => {
    // Permanently lock once sent to provider
    if (Boolean(rowData.sentToProvider)) return true;
    // Lock when claimed directly (e.g. legacy migrated claim, no provider invoice)
    if (Boolean(rowData.bonusClaimedDirect)) return true;
    // Lock when bonus invoice is paid or partially paid — use per-component lock flag
    if (Boolean(rowData.isBonusInvoiceLocked)) return true;
    const status = String(rowData.invoicePaymentStatus || "").toUpperCase();
    // Fallback: lock on overall invoice status only when no per-component data
    if (!rowData.hasBonusInvoice && (status === "PAID" || status === "PARTIALLY_PAID")) return true;
    return false;
  }, []);

  const isPendingTermScheduleRow = useCallback((rowData = {}) => Boolean(rowData._unimported), []);

  const { mutate: verifyEntry } = useMutation({
    mutationFn: (verificationId) =>
      axios.post(`${API_URL}/account-verification/${verificationId}/verify-and-import`),
    onSuccess: () => {
      toast.success("Verified");
      refetchVerifications();
      queryClient.invalidateQueries({ queryKey: ["admissionTerms", cadId] });
      onVerifySuccess?.();
    },
    onError: () => toast.error("Failed to verify"),
  });

  const { mutate: checkAndImport } = useMutation({
    mutationFn: (admissionTermId) => checkAndImportAdmissionTerm(admissionTermId),
    onSuccess: (response) => {
      if (response?.success) {
        toast.success(response?.message || "Term imported into accounts");
        refetchVerifications();
        queryClient.invalidateQueries({ queryKey: ["admissionTerms", cadId] });
        onVerifySuccess?.();
      } else {
        toast.info(response?.message || "Term is not eligible for import yet");
      }
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || "Failed to check import eligibility"),
  });

  const { mutate: rejectEntry, isLoading: isRejecting } = useMutation({
    mutationFn: ({ id, feedback }) =>
      axios.post(`${API_URL}/account-verification/${id}/reject`, { feedback }),
    onSuccess: () => {
      toast.success("Rejected");
      setRejectDialogId(null);
      setRejectFeedback("");
      refetchVerifications();
    },
    onError: () => toast.error("Failed to reject"),
  });

  // Add new admission detail
  const addStudentDetails = () => {
    setAccountDetails((prev) =>
      prev.map((item) =>
        item.uuid === parentId
          ? {
              ...item,
              admissionDetails: [
                ...item.admissionDetails,
                createNewAdmissionDetail(parentId, item.admissionDetails.length + 1),
              ],
            }
          : item
      )
    );
    onDirty?.();
  };

  // Handle input changes in subtable
  const handleSubInputChange = (e, row, type) => {
    const { name, value, checked } = e.target;
    //  if (
    //     //     differenceInDays(
    //     //       startOfDay(new Date(subRow.date)),
    //     //       startOfDay(new Date())
    //     //     ) > 0
    //     //   ) {

    const rowUuid = row.original?.uuid;
    const rowId = row.original?.id;
    setAccountDetails((prev) =>
      prev.map((item) =>
        item.uuid === parentId
          ? calculateRowValues({
              ...item,
              admissionDetails: item.admissionDetails.map((subItem, index) => {
                const matches = rowUuid
                  ? subItem.uuid === rowUuid
                  : rowId
                  ? subItem.id === rowId
                  : index === row.index;
                return matches
                  ? calculateSubRowValues({
                      ...subItem,
                      [name]: type === "switch" ? checked : value,
                    })
                  : subItem;
              }),
            })
          : item
      )
    );
    onDirty?.();
  };
  // Handle notes modal
  const handleToggleNotes = (item) => {
    setOpenNotesModal((prev) => ({
      state: !prev.state,
      id: item.id,
      data: item,
      parentId: parentId,
    }));
  };

  const handleClose = () => {
    setOpenNotesModal({ state: !openNotesModal?.state, id: null, data: null });
  };

  const handleToggleTermNotes = (rowData, row) => {
    const termNum = resolveInstallmentTermNumber(rowData, row);
    const termIndex = termNum - 1;
    setOpenTermNotesModal({ state: true, termIndex });
  };

  const resolveInstallmentTermNumber = (rowData, row) => {
    const rowIndex =
      row?.index ?? (data || []).findIndex((item) => item?.uuid && item.uuid === rowData?.uuid);
    const fallback = rowIndex >= 0 ? rowIndex + 1 : 1;
    return parseTermNumber(rowData?.terms, fallback);
  };

  const needsProviderEarlyImport = (rowData = {}) =>
    isAdmissionModule && isProviderSendLocked(rowData);

  const handleSendToProvider = (rowData, row) => {
    if (rowData?.sentToProvider) {
      toast.warning("This installment has already been sent to provider.");
      return;
    }
    if (!acountId) {
      toast.warning("Save all changes before sending this installment to provider.");
      return;
    }
    if (!rowData?.id && !rowData?.admissionTermId) {
      toast.warning("Save all changes before sending this installment to provider.");
      return;
    }

    const universityId = Number(parentRow?.admissionValues?.universityId);
    if (!Number.isFinite(universityId) || universityId <= 0) {
      toast.error(
        "Admission university is missing. Open the eye details and select the university first."
      );
      return;
    }

    setProviderSendDialog({
      rowData,
      row,
      stage: needsProviderEarlyImport(rowData) ? "warn-import" : "confirm-send",
    });
  };

  const handleProviderEarlyImport = async () => {
    const rowData = providerSendDialog?.rowData;
    const admissionTermId = rowData?.admissionTermId ?? null;
    if (!admissionTermId) {
      toast.error("No billing term is linked to this row — refresh the page and try again.");
      return;
    }
    setProviderSendBusy("importing");
    try {
      const response = await importAdmissionTermForProvider(admissionTermId);
      if (response?.success === false) {
        toast.info(
          response?.message || response?.errorMessage || "Term is not eligible for import yet"
        );
        return;
      }
      toast.success(response?.message || "Term imported into accounts");
      refetchVerifications();
      queryClient.invalidateQueries({ queryKey: ["admissionTerms", cadId] });
      onVerifySuccess?.();
      setProviderSendDialog((prev) => (prev ? { ...prev, stage: "confirm-send" } : prev));
    } catch (e) {
      toast.error(
        e?.response?.data?.errorMessage ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to import term"
      );
    } finally {
      setProviderSendBusy(null);
    }
  };

  const handleProviderSendConfirm = async () => {
    const { rowData, row } = providerSendDialog || {};
    if (!rowData) return;
    const universityId = Number(parentRow?.admissionValues?.universityId);
    if (!Number.isFinite(universityId) || universityId <= 0) {
      toast.error(
        "Admission university is missing. Open the eye details and select the university first."
      );
      return;
    }
    const provider = findProviderByUniversityId(providerList, universityId) || {
      universityId,
      universityName: parentRow?.admissionValues?.university,
    };

    setProviderSendBusy("sending");
    try {
      const accountProvider = await ensureProviderAccount(provider, refetchProvider);
      const admissionTermId = rowData.admissionTermId;
      let term = null;
      if (admissionTermId) {
        term = (admissionTerms || []).find((item) => Number(item?.id) === Number(admissionTermId));
      }
      if (!term?.id) {
        const generated = await axios.post(`${API_URL}/admission-terms/generate/${acountId}`);
        const termNumber = resolveInstallmentTermNumber(rowData, row);
        term = (generated?.data?.data || []).find(
          (item) => Number(item?.termNumber) === termNumber
        );
      }
      if (!term?.id) {
        toast.error("Could not find a matching billing term for this installment.");
        return;
      }

      await axios.post(`${API_URL}/provider-admissions/send`, {
        accountProviderId: accountProvider.accountProviderId,
        clientAccountDetailIds: [cadId],
        admissionTermIds: [term.id],
        admissionDetailIds: rowData.id ? [rowData.id] : null,
        agentName: parentRow?.admissionValues?.agentName || null,
      });
      setAccountDetails((prev) =>
        prev.map((item) =>
          item.uuid === parentId
            ? {
                ...item,
                admissionDetails: item.admissionDetails.map((subItem) =>
                  subItem.uuid === rowData.uuid ? { ...subItem, sentToProvider: true } : subItem
                ),
              }
            : item
        )
      );
      queryClient.invalidateQueries({ queryKey: ["admissionTerms", cadId] });
      toast.success("Installment sent to provider");
      setProviderSendDialog(null);
      navigate("/admin/account/provider/add", {
        state: { item: accountProvider, targetClientId: clientData?.id },
      });
    } catch (e) {
      toast.error(
        e?.response?.data?.errorMessage ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to send installment to provider"
      );
    } finally {
      setProviderSendBusy(null);
    }
  };

  // A legacy invoice with no per-component split covers the whole term, so it locks both toggles.
  const hasWholeTermInvoice = (rowData = {}) =>
    Boolean(rowData.hasInvoice) &&
    !Boolean(rowData.hasCommissionInvoice) &&
    !Boolean(rowData.hasBonusInvoice);

  // Only an invoice that already owns the component blocks the manual toggle — being sent to the
  // provider does not. That lets a mistaken manual claim on a sent term be undone and then settled
  // properly through a provider invoice.
  const commissionToggleLockReason = (rowData = {}) => {
    if (rowData._unimported) return "Term is not verified and imported yet.";
    if (Boolean(rowData.hasCommissionInvoice) || Boolean(rowData.isCommissionInvoiceLocked))
      return "A provider invoice controls this commission claim — change it from that invoice.";
    if (hasWholeTermInvoice(rowData))
      return "An invoice already covers this term — change the claim from that invoice.";
    return null;
  };
  const bonusToggleLockReason = (rowData = {}) => {
    if (rowData._unimported) return "Term is not verified and imported yet.";
    if (Boolean(rowData.hasBonusInvoice) || Boolean(rowData.isBonusInvoiceLocked))
      return "A provider invoice controls this bonus claim — change it from that invoice.";
    if (hasWholeTermInvoice(rowData))
      return "An invoice already covers this term — change the claim from that invoice.";
    return null;
  };

  const openClaimDialog = (tableRow, component, targetChecked) => {
    if (!cadId) {
      toast.warning("Save all changes before claiming commission or bonus.");
      return;
    }
    const rowData = tableRow.original;
    const admissionTermId = rowData.admissionTermId ?? null;
    if (!admissionTermId) {
      toast.error("No billing term is linked to this row — refresh the page and try again.");
      return;
    }
    const termLabel = rowData.terms ?? rowData.admissionTermNumber ?? admissionTermId;
    setClaimDialog({
      rowData,
      admissionTermId,
      termLabel,
      component,
      targetClaimed: targetChecked,
      note: "",
      mode: "real",
    });
  };

  const submitClaim = async () => {
    if (!claimDialog) return;
    const { rowData, admissionTermId, component, targetClaimed, note, mode } = claimDialog;
    // Preserve the OTHER component's current claim state so toggling one never clears the other.
    // `component` makes that authoritative server-side: the backend only moves the named component
    // and reads the other from the database, so a stale row copy can never flip it.
    const currentCommission = Boolean(rowData.commissionClaimedDirect);
    const currentBonus = Boolean(rowData.bonusClaimedDirect);
    const claimCommission = component === "commission" ? targetClaimed : currentCommission;
    const claimBonus = component === "bonus" ? targetClaimed : currentBonus;
    setClaimSubmitting(true);
    try {
      await axios.post(`${API_URL}/admission-terms/manual-claim`, {
        clientAccountDetailId: cadId,
        admissionTermId,
        termNumber: rowData.admissionTermNumber ?? parseTermNumber(rowData.terms, null),
        claimCommission,
        claimBonus,
        component: component === "commission" ? "COMMISSION" : "BONUS",
        affectsFinancials: mode === "real",
        note: note?.trim() || null,
      });
      toast.success(targetClaimed ? "Marked as claimed" : "Claim removed");
      setClaimDialog(null);
      queryClient.invalidateQueries({ queryKey: ["admissionTerms", cadId] });
      onVerifySuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to update claim");
    } finally {
      setClaimSubmitting(false);
    }
  };

  // Subtable columns
  const subColumns = useMemo(
    () => [
      {
        header: "Date",
        size: 150,
        accessorKey: "date",
        // sortingFn: "datetime",
        Cell: ({ row }) => (
          <InputField
            type="date"
            name="date"
            placeholder="Date"
            size="small"
            onChange={(e) => handleSubInputChange(e, row)}
            value={row.original.date}
            className="min-w-[100px]"
            {...admissionFieldLockedInputProps(row.original)}
          />
        ),
      },
      {
        header: "Census Date",
        size: 150,
        accessorKey: "censusDate",
        // sortingFn: "datetime",
        Cell: ({ row }) => (
          <InputField
            type="date"
            name="censusDate"
            placeholder="Census Date"
            size="small"
            onChange={(e) => handleSubInputChange(e, row)}
            value={formatDateForInput(row.original.censusDate)}
            className="min-w-[100px]"
            {...admissionFieldLockedInputProps(row.original)}
          />
        ),
      },
      {
        accessorKey: "terms",
        header: "Term",
        size: 50,
        enableSorting: true,
        sortingFn: (rowA, rowB, columnId) => {
          const valueA = rowA.original[columnId];
          const valueB = rowB.original[columnId];

          // Handle null/undefined/empty values
          if ((valueA == null || valueA === "") && (valueB == null || valueB === "")) return 0;
          if (valueA == null || valueA === "") return 1;
          if (valueB == null || valueB === "") return -1;

          // Convert to string and trim
          const strA = String(valueA).trim();
          const strB = String(valueB).trim();

          // Try to parse as numbers first for numeric terms
          const numA = Number(strA);
          const numB = Number(strB);

          // If both are valid numbers, sort numerically
          if (!isNaN(numA) && !isNaN(numB) && strA !== "" && strB !== "") {
            return numA - numB;
          }

          // Otherwise, sort as strings with numeric awareness
          return strA.localeCompare(strB, undefined, {
            numeric: true,
            sensitivity: "base",
          });
        },
        Cell: ({ row }) => {
          // return data?.findIndex((item) => item.uuid === row.original.uuid) + 1;
          const withdrawalState = row.original.withdrawalState;
          return (
            <div>
              <InputField
                type="text"
                name="terms"
                placeholder="Enter Term"
                value={row.original.terms || ""}
                onChange={(e) => handleSubInputChange(e, row)}
                disabled
              />
              {withdrawalState === "CANCELLED" && (
                <Chip label="Cancelled" size="small" sx={{ fontSize: 10, height: 18, mt: 0.5 }} />
              )}
              {withdrawalState === "SETTLEMENT" && (
                <Chip
                  label={
                    row.original.withdrawalSettlementMode === "PENDING"
                      ? "Settlement pending"
                      : "Settlement"
                  }
                  size="small"
                  color="warning"
                  sx={{ fontSize: 10, height: 18, mt: 0.5 }}
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "perSemFee",
        header: "Fee",
        size: 50,
        Cell: ({ row }) => (
          <InputField
            type="number"
            name="perSemFee"
            placeholder="Fee"
            onChange={(e) => handleSubInputChange(e, row)}
            value={row.original.perSemFee}
            className="min-w-[100px]"
            {...admissionFieldLockedInputProps(row.original)}
          />
        ),
      },
      {
        accessorKey: "materialFee",
        header: "Material/Enrollment",
        size: 50,
        Cell: ({ row }) => (
          <InputField
            type="number"
            name="materialFee"
            placeholder="Extra Fee"
            onChange={(e) => handleSubInputChange(e, row)}
            value={row.original.materialFee}
            className="min-w-[100px]"
            {...admissionFieldLockedInputProps(row.original)}
          />
        ),
      },
      {
        accessorKey: "discount",
        header: "Discount",
        size: 50,
        Cell: ({ row }) => (
          <InputField
            type="number"
            name="discount"
            placeholder="Discount"
            onChange={(e) => handleSubInputChange(e, row)}
            value={toNumber(row.original.discount)}
            className="min-w-[100px]"
            {...admissionFieldLockedInputProps(row.original)}
          />
        ),
      },
      {
        accessorKey: "agreed",
        header: "Agreed",
        size: 50,
        Cell: ({ row }) => {
          // A settled term is worth what it was settled at, not what it was scheduled at. Show both
          // so the number that changed is visible, rather than silently replacing the fee.
          const settled = row?.original?.withdrawalState === "SETTLEMENT";
          const scheduled =
            toNumber(row?.original?.perSemFee) +
            toNumber(row?.original?.materialFee) -
            toNumber(row?.original?.discount);
          return (
            <div className="flex items-center gap-2">
              {settled && (
                <span className="text-[11px] text-app-text-muted line-through shrink-0">
                  {scheduled.toFixed(2)}
                </span>
              )}
              <InputField
                type="number"
                name="agreed"
                placeholder="Agreed"
                disabled
                value={toNumber(row?.original?.agreed).toFixed(2)}
                className="min-w-[100px]"
                title={
                  settled ? `Settled at ${toNumber(row?.original?.agreed).toFixed(2)}` : undefined
                }
              />
            </div>
          );
        },
      },
      {
        accessorKey: "commission",
        header: "Commission",
        size: 50,
        Cell: ({ row }) => {
          const commissionLockReason = commissionToggleLockReason(row.original);
          const unimported = isPendingTermScheduleRow(row.original);
          const admissionTermId = row.original.admissionTermId;
          const pendingValue = pendingTermAmounts[admissionTermId]?.commission;
          return (
            <div className="flex items-center justify-start gap-2">
              <WrittenOffBadge
                retained={row.original.withdrawalCommissionRetained}
                withdrawalState={row.original.withdrawalState}
                what="commission"
              />
              <Tooltip title={commissionLockReason || "Toggle to claim/unclaim (no invoice)"} arrow>
                <span>
                  <FormControlLabel
                    control={<Switch color="primary" />}
                    label="Claimed"
                    labelPlacement="bottom"
                    name="isCommissionClaimed"
                    checked={isInstallmentCommissionClaimed(row.original)}
                    onChange={(e) => openClaimDialog(row, "commission", e.target.checked)}
                    disabled={!!commissionLockReason}
                  />
                </span>
              </Tooltip>
              <InputField
                type="number"
                name="commission"
                placeholder="Commission"
                onChange={(e) =>
                  unimported
                    ? setPendingTermAmounts((prev) => ({
                        ...prev,
                        [admissionTermId]: { ...prev[admissionTermId], commission: e.target.value },
                      }))
                    : handleSubInputChange(e, row)
                }
                onBlur={
                  unimported && admissionTermId
                    ? (e) =>
                        savePreImportAmount({
                          admissionTermId,
                          field: "commission",
                          value: e.target.value,
                        })
                    : undefined
                }
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                }}
                value={
                  unimported
                    ? pendingValue ?? row.original.commission ?? ""
                    : row.original.commission ?? ""
                }
                className="min-w-[100px]"
                disabled={(unimported && !admissionTermId) || isCommissionLocked(row.original)}
                title={
                  unimported
                    ? "Not claimable until this term is verified and imported, but you can set the amount now — it carries over once imported."
                    : isCommissionLocked(row.original)
                    ? "Commission locked: claimed or invoice paid"
                    : undefined
                }
              />
            </div>
          );
        },
      },
      {
        accessorKey: "bonus",
        header: "Bonus",
        size: 50,
        Cell: ({ row }) => {
          const bonusLockReason = bonusToggleLockReason(row.original);
          const unimported = isPendingTermScheduleRow(row.original);
          const admissionTermId = row.original.admissionTermId;
          const pendingValue = pendingTermAmounts[admissionTermId]?.bonus;
          return (
            <div className="flex items-center gap-2 justify-start">
              <WrittenOffBadge
                retained={row.original.withdrawalBonusRetained}
                withdrawalState={row.original.withdrawalState}
                what="bonus"
              />
              <Tooltip title={bonusLockReason || "Toggle to claim/unclaim (no invoice)"} arrow>
                <span>
                  <FormControlLabel
                    control={<Switch color="primary" />}
                    label="Claimed"
                    labelPlacement="bottom"
                    name="isBonusClaimed"
                    checked={isInstallmentBonusClaimed(row.original)}
                    onChange={(e) => openClaimDialog(row, "bonus", e.target.checked)}
                    disabled={!!bonusLockReason}
                  />
                </span>
              </Tooltip>
              <InputField
                type="number"
                name="bonus"
                placeholder="Bonus"
                onChange={(e) =>
                  unimported
                    ? setPendingTermAmounts((prev) => ({
                        ...prev,
                        [admissionTermId]: { ...prev[admissionTermId], bonus: e.target.value },
                      }))
                    : handleSubInputChange(e, row)
                }
                onBlur={
                  unimported && admissionTermId
                    ? (e) =>
                        savePreImportAmount({
                          admissionTermId,
                          field: "bonus",
                          value: e.target.value,
                        })
                    : undefined
                }
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                }}
                value={
                  unimported ? pendingValue ?? row.original.bonus ?? "" : row.original.bonus ?? ""
                }
                className="min-w-[100px]"
                disabled={(unimported && !admissionTermId) || isBonusLocked(row.original)}
                title={
                  unimported
                    ? "Not claimable until this term is verified and imported, but you can set the amount now — it carries over once imported."
                    : isBonusLocked(row.original)
                    ? "Bonus locked: claimed or invoice paid"
                    : undefined
                }
              />
            </div>
          );
        },
      },
      {
        accessorKey: "sentToProvider",
        header: "Provider",
        size: 90,
        Cell: ({ row }) =>
          row.original.sentToProvider ? (
            <Chip label="Sent" color="success" size="small" variant="outlined" />
          ) : (
            <Chip label="Not sent" size="small" variant="outlined" />
          ),
      },
      {
        id: "termNotifications",
        header: "Notifications",
        size: 110,
        enableSorting: false,
        Cell: ({ row }) => {
          const termNumber = resolveInstallmentTermNumber(row.original, row);
          return (
            <TermNotificationsCell
              count={termNotificationCounts[termNumber] || 0}
              clientAccountDetailId={cadId}
              termNumber={termNumber}
              termLabel={row.original?.terms ? `Term ${row.original.terms}` : `Term ${termNumber}`}
              clientId={clientData?.id}
              onModalClose={() =>
                fetchAccountTermNotificationCount({
                  clientAccountDetailId: cadId,
                  termNumber,
                  clientId: clientData?.id,
                }).then((count) =>
                  setTermNotificationCounts((prev) => ({ ...prev, [termNumber]: count }))
                )
              }
            />
          );
        },
      },
      {
        accessorKey: "providerStatus",
        header: "Status",
        size: 160,
        enableSorting: false,
        Cell: ({ row }) => {
          if (row.original._unimported) {
            if (!hasPendingVerificationPayment(row.original.payments)) {
              return (
                <Tooltip
                  title="No payment is waiting for verification. The term is imported once it is fully paid and verified."
                  arrow
                >
                  <Chip
                    label="Not imported"
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 10, height: 20 }}
                  />
                </Tooltip>
              );
            }
            return (
              <Tooltip title="Click to verify or reject this term" arrow>
                <Chip
                  label="Pending verification"
                  color="warning"
                  size="small"
                  clickable
                  onClick={() => setVerificationModalOpen(true)}
                  sx={{ fontSize: 10, height: 20 }}
                />
              </Tooltip>
            );
          }
          const {
            sentToProvider,
            hasInvoice,
            isCommissionClaimed,
            isBonusClaimed,
            hasCommissionInvoice,
            hasBonusInvoice,
            commissionInvoiceId,
            bonusInvoiceId,
            isCommissionInvoicePaid,
            isBonusInvoicePaid,
            commissionClaimedDirect,
            bonusClaimedDirect,
            commissionChipOnly,
            bonusChipOnly,
            claimNote,
          } = row.original;
          // Distinct tooltip for a direct (invoice-free) claim: prefer the stored note (manual claims
          // set their own; legacy migrated claims carry the migration note), then flag the money mode.
          const directClaimTitle = (component, chipOnly) =>
            `${component} claimed — ${claimNote || "no provider invoice"}${
              chipOnly
                ? " (chip only: invoice kept outside this system, counts in Cost/PL)"
                : " (counts in Cost/PL)"
            }`;
          // Use actual invoice paid status — isCommissionClaimed/isBonusClaimed flags are corrupted for old records.
          // commissionClaimedDirect/bonusClaimedDirect are clean (claimedDate-backed) direct claims, e.g. legacy migrated.
          const commClaimed = isInstallmentCommissionClaimed(row.original);
          const bonusClaimed = isInstallmentBonusClaimed(row.original);
          // separate invoices when both components have distinct IDs, or only one component invoiced
          const hasSeparateInvoices = hasCommissionInvoice || hasBonusInvoice;
          if (
            !sentToProvider &&
            !hasInvoice &&
            !commClaimed &&
            !bonusClaimed &&
            !hasSeparateInvoices
          )
            return null;
          const { invoiceId } = row.original;
          const universityId = Number(parentRow?.admissionValues?.universityId);
          const provider = findProviderByUniversityId(providerList, universityId) || {
            universityId,
            universityName: parentRow?.admissionValues?.university,
          };
          const goToProvider = () =>
            navigate("/admin/account/provider/add", {
              state: {
                item: {
                  accountProviderId: provider?.accountProviderId,
                  universityId: provider?.universityId,
                  universityName: provider?.universityName,
                },
                targetClientId: clientData?.id,
              },
            });
          const goToInvoice = (id) => id && navigate(`/admin/invoice/edit/${id}`);
          return (
            <div className="flex flex-wrap gap-1">
              {sentToProvider && !hasInvoice && (
                <Tooltip title="Click to view in Provider Accounts" arrow>
                  <Chip
                    label="Sent"
                    color="info"
                    size="small"
                    clickable
                    onClick={goToProvider}
                    sx={{ fontSize: 10, height: 20 }}
                  />
                </Tooltip>
              )}
              {/* Separate commission invoice chip */}
              {hasCommissionInvoice && commissionInvoiceId && (
                <Tooltip title={`Comm Invoice #${commissionInvoiceId} — click to view`} arrow>
                  <Chip
                    label={commClaimed ? `Comm ✓` : `Comm #${commissionInvoiceId}`}
                    color={commClaimed ? "success" : "warning"}
                    size="small"
                    clickable
                    onClick={() => goToInvoice(commissionInvoiceId)}
                    sx={{ fontSize: 10, height: 20 }}
                  />
                </Tooltip>
              )}
              {/* Separate bonus invoice chip */}
              {hasBonusInvoice && bonusInvoiceId && (
                <Tooltip title={`Bonus Invoice #${bonusInvoiceId} — click to view`} arrow>
                  <Chip
                    label={bonusClaimed ? `Bonus ✓` : `Bonus #${bonusInvoiceId}`}
                    color={bonusClaimed ? "success" : "warning"}
                    size="small"
                    clickable
                    onClick={() => goToInvoice(bonusInvoiceId)}
                    sx={{ fontSize: 10, height: 20 }}
                  />
                </Tooltip>
              )}
              {/* Combined invoice fallback — only when no per-component IDs available */}
              {hasInvoice && !hasSeparateInvoices && (
                <Tooltip
                  title={invoiceId ? `Invoice #${invoiceId} — click to view` : "Invoice generated"}
                  arrow
                >
                  <Chip
                    label="Invoiced"
                    color="warning"
                    size="small"
                    clickable
                    onClick={() => goToInvoice(invoiceId)}
                    sx={{ fontSize: 10, height: 20 }}
                  />
                </Tooltip>
              )}
              {/* Direct (invoice-free) claim chips — e.g. legacy migrated claims */}
              {commissionClaimedDirect && !hasCommissionInvoice && (
                <Tooltip title={directClaimTitle("Commission", commissionChipOnly)} arrow>
                  <Chip
                    label="Comm ✓"
                    color={commissionChipOnly ? "default" : "success"}
                    size="small"
                    variant={commissionChipOnly ? "outlined" : "filled"}
                    sx={{ fontSize: 10, height: 20 }}
                  />
                </Tooltip>
              )}
              {bonusClaimedDirect && !hasBonusInvoice && (
                <Tooltip title={directClaimTitle("Bonus", bonusChipOnly)} arrow>
                  <Chip
                    label="Bonus ✓"
                    color={bonusChipOnly ? "default" : "success"}
                    size="small"
                    variant={bonusChipOnly ? "outlined" : "filled"}
                    sx={{ fontSize: 10, height: 20 }}
                  />
                </Tooltip>
              )}
            </div>
          );
        },
      },
      // {
      //   accessorKey: "verification",
      //   header: "Verification",
      //   size: 220,
      //   enableSorting: false,
      //   Cell: ({ row }) => {
      //     const isPaid = ["PAID", "PARTIALLY_PAID"].includes(row.original.invoicePaymentStatus);
      //     const rowVerification = getRowVerification(row.original);
      //     const canSend = isPaid && row.original.id &&
      //       (!rowVerification || rowVerification.status === "DRAFT" || rowVerification.status === "REJECTED");
      //     const canVerifyOrReject =
      //       canVerifyOrRejectVerification && rowVerification?.status === "PENDING";
      //     return (
      //       <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      //         <AccountVerificationChip
      //           status={rowVerification?.status}
      //           rejectionFeedback={rowVerification?.rejectionFeedback}
      //         />
      //         {canSend && (
      //           <Button
      //             size="small"
      //             variant="outlined"
      //             onClick={() => {
      //               setSendDialogIds([acountId]);
      //               setSendDialogAdmissionDetailIds([row.original.id]);
      //               setSendDialogSnapshots({
      //                 [acountId]: { ...parentRow, admissionDetails: [row.original] },
      //               });
      //               setSendDialogOpen(true);
      //             }}
      //           >
      //             Send
      //           </Button>
      //         )}
      //         {canVerifyOrReject && (
      //           <>
      //             <Button size="small" color="success" variant="contained"
      //               onClick={() => verifyEntry(rowVerification.id)}>
      //               Verify
      //             </Button>
      //             <Button size="small" color="error" variant="outlined"
      //               onClick={() => setRejectDialogId(rowVerification.id)}>
      //               Reject
      //             </Button>
      //           </>
      //         )}
      //         <AccountVerificationAuditLogExpander auditLogs={rowVerification?.auditLogs ?? []} />
      //       </Box>
      //     );
      //   },
      // },
      {
        accessorKey: "perSemCost",
        header: "Cost",
        size: 50,
        Cell: ({ row }) => (
          <InputField
            type="number"
            name="perSemCost"
            placeholder="Cost"
            value={toNumber(row.original.perSemCost).toFixed(2)}
            disabled
            className="min-w-[100px]"
          />
        ),
      },
      {
        accessorKey: "paidAmount",
        header: "Paid Amount",
        size: 120,
        Cell: ({ row, cell }) => (
          <Box>
            <span>{formatMoney(cell.getValue() || 0)}</span>
            {row.original._pendingPaid > 0 && (
              <Typography
                component="span"
                variant="caption"
                color="warning.main"
                sx={{ display: "block", lineHeight: 1.3 }}
              >
                +{formatMoney(row.original._pendingPaid)} pending
              </Typography>
            )}
          </Box>
        ),
      },
      {
        id: "termPayments",
        header: "Payments",
        size: 60,
        Cell: ({ row }) => {
          const termId = row.original.admissionTermId;
          if (!termId) return null;
          const count = (row.original.payments || []).length;
          const refundOwed =
            row.original.withdrawalState === "SETTLEMENT"
              ? Math.max(
                  0,
                  netVerifiedPaidFromTermPayments(row.original.payments) -
                    toNumber(row.original.settledAgreed)
                )
              : 0;
          const credit = canRequestCreditAdjustment()
            ? termCreditAvailable(row.original.payments, row.original.agreed)
            : 0;
          return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              {credit > 0 && (
                <Tooltip title="Paid more than agreed. Click to move some of it to another term of this admission.">
                  <Chip
                    label={`Credit ${formatMoney(credit)}`}
                    size="small"
                    color="success"
                    variant="outlined"
                    onClick={() =>
                      setCreditMove({
                        termId,
                        termLabel: `T${row.original.admissionTermNumber ?? ""}`,
                      })
                    }
                    sx={{ fontSize: 10, height: 18, mb: 0.5 }}
                  />
                </Tooltip>
              )}
              {refundOwed > 0 && (
                <Tooltip title="The student paid more than the settled amount. Record a refund here; it goes for verification like any other entry.">
                  <Chip
                    label={`Refund ${formatMoney(refundOwed)}`}
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => setTermPaymentsModal({ open: true, admissionTermId: termId })}
                    sx={{ fontSize: 10, height: 18, mb: 0.5 }}
                  />
                </Tooltip>
              )}
              <Tooltip title="View / add payments">
                <IconButton
                  size="small"
                  onClick={() => setTermPaymentsModal({ open: true, admissionTermId: termId })}
                >
                  <Badge
                    badgeContent={count}
                    color="secondary"
                    max={99}
                    sx={{ "& .MuiBadge-badge": { fontSize: 9, minWidth: 14, height: 14 } }}
                  >
                    <FaPlusCircle className="text-blue-500" />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        accessorKey: "overPaid",
        header: "Over Paid",
        size: 50,
        Cell: ({ row }) => {
          const overPaid = toNumber(row.original.paidAmount) - toNumber(row.original.agreed);
          return (
            <InputField
              type="number"
              name="overPaid"
              placeholder="Over Paid"
              size="small"
              disabled
              value={overPaid > 0 ? overPaid : 0}
              className={`min-w-[100px] ${overPaid > 0 ? "text-green-600" : ""}`}
            />
          );
        },
      },
      {
        accessorKey: "dueAmount",
        header: "Due",
        size: 50,
        Cell: ({ row }) => (
          <InputField
            type="number"
            name="dueAmount"
            placeholder="Due Amount"
            size="small"
            value={
              toNumber(row.original.dueAmount) > 0
                ? toNumber(row.original.dueAmount).toFixed(2)
                : "0.00"
            }
            disabled
            className="min-w-[100px]"
          />
        ),
      },
      {
        accessorKey: "referral",
        header: "Referral",
        size: 100,
        // Referral is a Direct-Account-only field (it never feeds the term schedule or provider invoice),
        // so it is editable regardless of verification/lock/paid status — but only once the installment is
        // imported into the account. An unimported (pending) term has no account row to persist it to, so
        // the input is disabled there.
        Cell: ({ row }) => {
          const isUnimported = Boolean(row.original._unimported);
          return (
            <InputField
              type="number"
              name="referral"
              placeholder="Referral"
              size="small"
              disabled={isUnimported}
              title={
                isUnimported
                  ? "Referral can be set once this term is imported into the account"
                  : undefined
              }
              onChange={(e) => handleSubInputChange(e, row)}
              value={toNumber(row.original.referral)}
              className="min-w-[100px]"
            />
          );
        },
      },
      {
        accessorKey: "profitLoss",
        header: "Profit Loss",
        size: 120,
        Cell: ({ row }) => (
          <InputField
            type="number"
            name="profitLoss"
            placeholder="Profit/Loss"
            size="small"
            value={toNumber(row.original.profitLoss).toFixed(2)}
            disabled
            colorize
          />
        ),
      },
      {
        accessorKey: "Action",
        header: "Action",
        size: 20,
        Cell: ({ row }) => (
          <div className="flex items-center">
            {/* <Tooltip title="Generate Invoice" arrow>
              <IconButton onClick={() => handleGenerateInvoice(row.original, row)}>
                <FaFileInvoice className="text-orange-400 cursor-pointer" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy Row" arrow>
              <IconButton onClick={() => handleCopyRow(row.original)}>
                <CopyAllOutlined className="text-orange-400 cursor-pointer" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Notes" arrow>
              <span>
                <IconButton onClick={() => handleToggleNotes(row.original)}>
                  <Note className="text-orange-400 cursor-pointer" />
                {row.original.notes?.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {row.original.notes.length}
                  </span>
                )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                row.original.sentToProvider
                  ? "Already sent to provider"
                  : "Send to Provider Accounts"
              }
              arrow
            >
              <span>
                <IconButton
                  disabled={row.original.sentToProvider}
                  onClick={() => handleSendToProvider(row.original, row)}
                >
                  <Send
                    className={
                      row.original.sentToProvider
                        ? "text-gray-300"
                        : "text-orange-400 cursor-pointer"
                    }
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete Entry" arrow>
              <IconButton onClick={() => handleDeleteStudentDetails(row.original.uuid)}>
                <AiFillDelete className="text-red-600 cursor-pointer" />
              </IconButton>
            </Tooltip> */}
            <RowActionMenu
              row={row}
              onGenerateInvoice={null}
              onCopyRow={null}
              onToggleNotes={
                row.original.admissionTermId
                  ? // Admission term rows: notes anchor to the term, so available before import too.
                    canViewAccountNotes
                    ? () => setAccountTermNotesModal({ open: true, row: row.original })
                    : null
                  : !canViewAccountNotes ||
                    isPendingTermScheduleRow(row.original) ||
                    !row.original?.id
                  ? null
                  : () => handleToggleNotes(row.original)
              }
              accountNotesDisabledReason={
                !canViewAccountNotes
                  ? "You need the Accounts permission to view account notes."
                  : !row.original.admissionTermId &&
                    (isPendingTermScheduleRow(row.original) || !row.original?.id)
                  ? "Installment notes are available after this term is imported into the account."
                  : undefined
              }
              onViewTermNotes={() => handleToggleTermNotes(row.original, row)}
              termNotesCount={
                termNotesCounts[parseTermNumber(row.original?.terms, row.index + 1) - 1] || 0
              }
              onCheckAndImport={
                row.original._unimported &&
                toNumber(row.original.dueAmount) <= 0 &&
                row.original.admissionTermId
                  ? () => checkAndImport(row.original.admissionTermId)
                  : null
              }
              isSentToProvider={row.original.sentToProvider}
              onSendToProvider={() => handleSendToProvider(row.original, row)}
              isProviderSendDisabled={Boolean(row.original.sentToProvider)}
              providerSendDisabledReason={undefined}
              onViewAdmission={
                parentRow?.admissionValues?.studentDbId
                  ? () => navigate(`/admin/admission/fees/${parentRow.admissionValues.studentDbId}`)
                  : null
              }
              onViewInvoice={
                row.original.hasInvoice && row.original.invoiceId
                  ? () => navigate(`/admin/invoice/edit/${row.original.invoiceId}`)
                  : null
              }
              onViewProvider={
                row.original.sentToProvider
                  ? () => {
                      const universityId = Number(parentRow?.admissionValues?.universityId);
                      const provider = findProviderByUniversityId(providerList, universityId) || {
                        universityId,
                        universityName: parentRow?.admissionValues?.university,
                      };
                      navigate("/admin/account/provider/add", {
                        state: {
                          item: {
                            accountProviderId: provider?.accountProviderId,
                            universityId: provider?.universityId,
                            universityName: provider?.universityName,
                          },
                          targetClientId: clientData?.id,
                        },
                      });
                    }
                  : null
              }
              onDelete={null}
              onSendAdminNotification={() =>
                setNotifyModal({ open: true, row: row.original, rowIndex: row.index })
              }
              onForceDeleteTerm={
                isAdmissionModule &&
                superAdmin &&
                row.original?.id &&
                !isPendingTermScheduleRow(row.original)
                  ? () => setForceDeleteTerm(resolveInstallmentTermNumber(row.original, row))
                  : null
              }
            />
          </div>
        ),
      },
    ],
    [
      accountDetails,
      parentId,
      acountId,
      providerList,
      parentRow,
      refetchProvider,
      navigate,
      setAccountDetails,
      clientData,
      isAdmissionInvoiceLocked,
      isVerificationLocked,
      installmentLockTitle,
      lockedInputProps,
      verificationLockedInputProps,
      admissionFieldLockedInputProps,
      admissionPaidLockedInputProps,
      verificationByAdmissionDetailId,
      canVerifyOrRejectVerification,
      verifyEntry,
      checkAndImport,
      termNotesCounts,
      termNotificationCounts,
      cadId,
      pendingTermAmounts,
      savePreImportAmount,
    ]
  );

  const isAdmission = parentRow?.module === "Admission";
  const filteredData = useMemo(() => {
    if (!isAdmission) return data || [];
    // Before COE and before any verified payment, only Term 1 is shown (pending COE-fee verification).
    if (
      !shouldShowAllAdmissionTerms(parentRow, {
        admissionTerms,
      })
    ) {
      return mergedData.filter((row) => parseTermNumber(row?.terms, null) === 1);
    }
    return mergedData;
  }, [isAdmission, mergedData, data, parentRow, admissionTerms]);

  // Material table configuration
  const secondTable = useMaterialReactTable({
    columns: subColumns,
    data: filteredData,
    muiTableBodyRowProps: ({ row }) => {
      if (row.original.withdrawalState === "CANCELLED") {
        return { sx: { opacity: 0.55, "& td": { textDecoration: "line-through" } } };
      }
      return row.original._unimported ? { sx: { backgroundColor: "rgba(255, 193, 7, 0.06)" } } : {};
    },
    enablePagination: false,
    initialState: {
      density: "compact",
      columnVisibility: {
        sentToProvider: false,
        providerStatus: true,
      },
      columnPinning: {
        left: ["Action"],
      },
      sorting: [
        {
          id: "terms",
          desc: false,
        },
      ],
    },
    enableSortingRemoval: false, // Prevent removing the default sort
    enableColumnPinning: true,
    enableSorting: true,
    enableStickyHeader: true,
    muiTableContainerProps: {
      sx: {
        height: "500px",
        maxHeight: "1000px",
        borderWidth: "0px",
      },
    },
    renderTopToolbarCustomActions: () => {
      const atTermLimit = isAdmissionModule;
      return (
        <div className="flex justify-start items-center gap-12">
          <h1 className="text-xl font-normal">Installment Entries</h1>
          {onBackToSummary && (
            <Tooltip title="This admission is completed — switch back to the compact summary view">
              <Button
                variant="outlined"
                color="success"
                startIcon={<SummarizeOutlined />}
                onClick={onBackToSummary}
              >
                Summary view
              </Button>
            </Tooltip>
          )}
          {isAdmissionModule && superAdmin && swappableTermNumbers.length >= 2 && (
            <Tooltip title="Swap all data between two imported terms (super admin)">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<SwapHoriz />}
                onClick={() => setTermSwapOpen(true)}
              >
                Swap terms
              </Button>
            </Tooltip>
          )}
          {isAdmissionModule && pendingVerificationCount > 0 && (
            <Tooltip
              title="Verify or reject pending term & payment verifications for this account"
              arrow
            >
              <Button
                variant="outlined"
                color="warning"
                startIcon={<TaskAltOutlined />}
                onClick={() => setVerificationModalOpen(true)}
              >
                Verifications ({pendingVerificationCount})
              </Button>
            </Tooltip>
          )}
          {!isAdmission && (
            <Tooltip title="Installment rows are managed from the selected admission term schedule.">
              <span>
                <Button
                  variant="contained"
                  startIcon={<FaPlusCircle />}
                  onClick={addStudentDetails}
                  disabled={atTermLimit}
                >
                  Add Entries
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip
            title={
              <div className="p-3 min-w-[520px] max-w-[580px] bg-app-surface text-app-text shadow-lg rounded-md">
                <div className="font-semibold text-base">Calculation Rules</div>

                <div className="font-semibold mt-3 text-sm">For Each Installment:</div>
                <p className="text-sm mt-1">
                  • <strong>Agreed Amount</strong> = Fee + Extra Fee - Discount
                </p>
                <p className="text-sm">
                  • <strong>Cost</strong> = Fee + Extra Fee - Commission (if claimed) - Bonus (if
                  claimed)
                </p>
                <p className="text-sm">
                  • <strong>Due Amount</strong> = Fee + Extra Fee - Discount - Paid Amount
                </p>
                <p className="text-sm">
                  • <strong>Profit/Loss</strong> = Agreed Amount - Cost - Due Amount - Referral
                </p>

                <div className="font-semibold mt-3 text-sm">Totals (Admission Only):</div>
                <p className="text-sm mt-1">
                  Parent row totals sum every term below, imported or not.
                </p>
                <p className="text-sm">
                  Commission and Bonus count once the linked provider invoice is marked paid, or
                  when claimed manually, chip-only claims included.
                </p>
                <p className="text-sm">
                  Profit/Loss counts a term only once its Date is reached (an imported term with no
                  Date counts now; a term not imported yet needs a Date). A future term shows what
                  it will cost and owe, but no profit or loss yet. Due Amount counts every term;
                  "Effective" due keeps to reached terms.
                </p>

                <div className="font-semibold mt-3 text-sm">Important Note:</div>
                <p className="text-sm">
                  A withdrawal changes these figures: a settled term is worth what it was settled
                  at, a cancelled term agrees, costs and owes nothing, and commission or bonus on
                  either counts only when it was explicitly retained.
                </p>

                <p className="text-xs text-orange-600 mt-3 italic">
                  Tip: Claims flow from paid provider invoices, or toggle Commission/Bonus to claim
                  directly (no invoice). Once a term is sent to provider its claim is
                  invoice-driven.
                </p>
              </div>
            }
            arrow
            placement="top"
          >
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center justify-center bg-orange-500 text-white rounded-full w-7 h-7">
                <InfoOutlined style={{ fontSize: 18 }} className="text-white" />
              </div>
              <span className="text-sm text-app-text font-medium">Guide</span>
            </div>
          </Tooltip>
        </div>
      );
    },
  });

  return (
    <div className="instalment-entries-table">
      {settlementRow && (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={
            onAdjustSettlement ? (
              <Button color="inherit" size="small" onClick={onAdjustSettlement}>
                Settlement
              </Button>
            ) : undefined
          }
        >
          {settlementRow.withdrawalSettlementMode === "PENDING"
            ? `Application withdrawn at ${settlementRow.terms}. The settlement is still to be decided; later terms are already cancelled.`
            : `Application withdrawn at ${settlementRow.terms}. Later terms are cancelled and no longer count towards due or profit/loss.`}
        </Alert>
      )}
      {summaryMode ? (
        <CompletedInstallmentsSummary
          rows={filteredData}
          termNotesCounts={termNotesCounts}
          canViewAccountNotes={canViewAccountNotes}
          onOpenNotes={(rowData) => handleToggleNotes(rowData)}
          onOpenTermNotes={(rowData, index) => handleToggleTermNotes(rowData, { index })}
          onEdit={onRequestFullView}
        />
      ) : (
        <MaterialReactTable table={secondTable} />
      )}
      {openNotesModal.state && canViewAccountNotes && (
        <NotesModal
          open={openNotesModal.state}
          handleClose={handleClose}
          data={openNotesModal.data}
          accountDetails={accountDetails}
          setAccountDetails={setAccountDetails}
          subtable={true}
          parentId={parentId}
          onLocalNoteAdded={onLocalNoteAdded}
        />
      )}
      {accountTermNotesModal.open && canViewAccountNotes && (
        <NotesModal
          open={accountTermNotesModal.open}
          handleClose={() => setAccountTermNotesModal({ open: false, row: null })}
          standalone
          standaloneParentType="ADMISSION_TERM"
          standaloneParentId={accountTermNotesModal.row?.admissionTermId}
          initialNotes={accountTermNotesModal.row?._termNotes || []}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ["admissionTerms", cadId] })}
        />
      )}
      {openTermNotesModal.state && (
        <DiscussionNotesModal
          open={openTermNotesModal.state}
          handleClose={() => {
            const closedIndex = openTermNotesModal.termIndex;
            setOpenTermNotesModal({ state: false, termIndex: -1 });
            if (clientData?.id && admissionIdForTermNotes != null && closedIndex >= 0) {
              axios
                .get(
                  `${API_URL}/discussion/${clientData.id}?type=admission_term_${admissionIdForTermNotes}_${closedIndex}`
                )
                .then((r) => {
                  const count = (r.data?.comments ?? r.data?.data ?? []).length;
                  setTermNotesCounts((prev) => ({ ...prev, [closedIndex]: count }));
                })
                .catch(() => {});
            }
          }}
          studentId={clientData?.id}
          type={`admission_term_${parentRow?.admissionValues?.studentDbId}_${openTermNotesModal.termIndex}`}
          attachmentsEnabled={true}
        />
      )}
      <Dialog
        open={!!rejectDialogId}
        onClose={() => {
          setRejectDialogId(null);
          setRejectFeedback("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Verification</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Feedback"
            value={rejectFeedback}
            onChange={(e) => setRejectFeedback(e.target.value)}
            placeholder="Explain why this entry is rejected..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectDialogId(null);
              setRejectFeedback("");
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => rejectEntry({ id: rejectDialogId, feedback: rejectFeedback })}
            disabled={!rejectFeedback.trim() || isRejecting}
          >
            {isRejecting ? "Rejecting..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!claimDialog}
        onClose={() => (claimSubmitting ? null : setClaimDialog(null))}
        maxWidth="sm"
        fullWidth
      >
        {claimDialog && (
          <>
            <DialogTitle>
              {claimDialog.targetClaimed ? "Claim" : "Remove claim for"}{" "}
              {claimDialog.component === "commission" ? "Commission" : "Bonus"} — Term{" "}
              {claimDialog.termLabel}
            </DialogTitle>
            <DialogContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {claimDialog.targetClaimed
                  ? "No invoice will be generated. This marks the amount as claimed directly on the account (independent of the provider invoice flow)."
                  : "This removes the direct claim mark. No invoice is affected."}
              </Alert>
              {Boolean(claimDialog.rowData?.sentToProvider) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {claimDialog.targetClaimed
                    ? `This term is already sent to the provider. Claiming the ${claimDialog.component} here settles it without an invoice and marks it claimed on the provider term too — no invoice can be generated for it until this claim is removed.`
                    : `This term is already sent to the provider and the ${claimDialog.component} was claimed manually, with no invoice behind it. Removing the claim leaves it unclaimed on both this account and the provider term — claim it again by generating a provider invoice for the ${claimDialog.component} and marking that invoice paid.`}
                </Alert>
              )}
              {claimDialog.targetClaimed && (
                <FormControl sx={{ mb: 2 }}>
                  <FormLabel>Money impact</FormLabel>
                  <RadioGroup
                    value={claimDialog.mode}
                    onChange={(e) => setClaimDialog((prev) => ({ ...prev, mode: e.target.value }))}
                  >
                    <FormControlLabel
                      value="real"
                      control={<Radio />}
                      label="Treat as a real claim — reduces Cost and Profit/Loss and counts toward totals"
                    />
                    <FormControlLabel
                      value="chip"
                      control={<Radio />}
                      label="Chip + lock only — mark claimed without changing money figures (e.g. already invoiced elsewhere)"
                    />
                  </RadioGroup>
                </FormControl>
              )}
              {/*
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Note (optional)"
                value={claimDialog.note}
                onChange={(e) => setClaimDialog((prev) => ({ ...prev, note: e.target.value }))}
                placeholder="Manually claimed (no invoice generated)."
              />
              */}
            </DialogContent>
            <DialogActions>
              <Button disabled={claimSubmitting} onClick={() => setClaimDialog(null)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color={claimDialog.targetClaimed ? "primary" : "error"}
                disabled={claimSubmitting}
                onClick={submitClaim}
              >
                {claimSubmitting
                  ? "Saving..."
                  : claimDialog.targetClaimed
                  ? "Confirm claim"
                  : "Remove claim"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Dialog
        open={!!providerSendDialog}
        onClose={() => (providerSendBusy ? null : setProviderSendDialog(null))}
        maxWidth="sm"
        fullWidth
      >
        {providerSendDialog &&
          (() => {
            const rowData = providerSendDialog.rowData || {};
            const termLabel =
              rowData.terms ??
              (rowData.admissionTermNumber != null
                ? `T${rowData.admissionTermNumber}`
                : "this term");
            const paid = toNumber(rowData.paidAmount);
            const agreed = toNumber(rowData.agreed);
            const due = toNumber(rowData.dueAmount);
            const unsettled = due > 0.005 || paid < agreed - 0.005;
            if (providerSendDialog.stage === "warn-import") {
              return (
                <>
                  <DialogTitle>Import before sending to provider?</DialogTitle>
                  <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Student payments for {termLabel} may not be fully settled and the term is
                      still pending. Do you want to import it first?
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                      Paid ${paid.toFixed(2)} of ${agreed.toFixed(2)}
                      {due > 0.005 ? ` — $${due.toFixed(2)} still due` : ""}. Importing does not
                      verify any pending student payments; those still need verification separately.
                    </Typography>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      disabled={providerSendBusy != null}
                      onClick={() => setProviderSendDialog(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      disabled={providerSendBusy != null}
                      onClick={handleProviderEarlyImport}
                    >
                      {providerSendBusy === "importing" ? "Importing..." : "Yes, import first"}
                    </Button>
                  </DialogActions>
                </>
              );
            }
            return (
              <>
                <DialogTitle>Send to provider accounts?</DialogTitle>
                <DialogContent>
                  {unsettled && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Student has not fully settled {termLabel} (paid ${paid.toFixed(2)} of $
                      {agreed.toFixed(2)}). It can still be sent, and a provider invoice can still
                      be generated.
                    </Alert>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Send {termLabel} to the provider accounts?
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button
                    disabled={providerSendBusy != null}
                    onClick={() => setProviderSendDialog(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    disabled={providerSendBusy != null}
                    onClick={handleProviderSendConfirm}
                  >
                    {providerSendBusy === "sending" ? "Sending..." : "Send to provider"}
                  </Button>
                </DialogActions>
              </>
            );
          })()}
      </Dialog>
      {verificationModalOpen && (
        <AccountVerificationModal
          open={verificationModalOpen}
          onClose={() => setVerificationModalOpen(false)}
          clientId={clientData?.id}
          admissionId={admissionIdForTermNotes}
          clientAccountDetailId={cadId}
          canAct={canVerifyOrRejectVerification}
          autoCloseWhenEmpty={autoCloseVerificationWhenEmpty}
          onActionComplete={handleVerificationActionComplete}
        />
      )}
      {termSwapOpen && (
        <TermSwapDialog
          open={termSwapOpen}
          onClose={() => setTermSwapOpen(false)}
          clientAccountDetailId={clientAccountDetailId}
          swappableTerms={swappableTermNumbers}
          hasPaidInvoiceWarning={swapPaidInvoiceWarning}
          onSuccess={handleTermSwapSuccess}
        />
      )}

      <ForceDeleteTermDialog
        open={forceDeleteTerm != null}
        onClose={() => setForceDeleteTerm(null)}
        cadId={clientAccountDetailId}
        termNumber={forceDeleteTerm}
        onDeleted={() => {
          refetchVerifications();
          queryClient.invalidateQueries({ queryKey: ["admissionTerms", cadId] });
          onVerifySuccess?.();
        }}
      />

      {termPaymentsModal.open && termPaymentsModalRow && (
        <TermPaymentsModal
          open={termPaymentsModal.open}
          onClose={() => setTermPaymentsModal({ open: false, admissionTermId: null })}
          termLabel={`T${termPaymentsModalRow.admissionTermNumber ?? ""}`}
          clientName={clientData?.name || ""}
          agreedAmount={toNumber(termPaymentsModalRow.agreed)}
          payments={termPaymentsDraft}
          readOnly={!isPendingTermScheduleRow(termPaymentsModalRow)}
          onChangePayments={setTermPaymentsDraft}
          termId={termPaymentsModalRow.admissionTermId}
          backendPayments={termPaymentsModalRow.payments || []}
          onPaymentsSynced={() =>
            queryClient.invalidateQueries({ queryKey: ["admissionTerms", cadId] })
          }
        />
      )}

      {creditMove && (
        <CreditAdjustmentDialog
          open
          onClose={() => setCreditMove(null)}
          termId={creditMove.termId}
          termLabel={creditMove.termLabel}
          onDone={() => {
            queryClient.invalidateQueries({ queryKey: ["admissionTerms", cadId] });
            onVerifySuccess?.();
          }}
        />
      )}

      <SendNotificationModal
        open={notifyModal.open}
        onClose={() => setNotifyModal({ open: false, row: null, rowIndex: null })}
        users={usersList || []}
        context={{
          clientId: clientData?.id ?? null,
          clientAccountDetailId: cadId,
          admissionId: parentRow?.admissionValues?.studentDbId ?? null,
          admissionTermId: notifyModal.row?.admissionTermId ?? null,
          termNumber: notifyModal.row
            ? parseTermNumber(
                notifyModal.row.terms,
                notifyModal.row.admissionTermNumber ?? (notifyModal.rowIndex ?? 0) + 1
              )
            : null,
          caseOfficerId: clientData?.caseOfficerId ?? null,
          caseOfficer: clientData?.caseOfficer ?? null,
        }}
        onSent={() => {
          const termNumber = notifyModal.row
            ? parseTermNumber(
                notifyModal.row.terms,
                notifyModal.row.admissionTermNumber ?? (notifyModal.rowIndex ?? 0) + 1
              )
            : null;
          if (!termNumber || !cadId || !clientData?.id) return;
          fetchAccountTermNotificationCount({
            clientAccountDetailId: cadId,
            termNumber,
            clientId: clientData.id,
          }).then((count) =>
            setTermNotificationCounts((prev) => ({ ...prev, [termNumber]: count }))
          );
        }}
      />
    </div>
  );
};

export default AddAccountSecondTable;

const RowActionMenu = ({
  row,
  onGenerateInvoice,
  onCopyRow,
  onToggleNotes,
  accountNotesDisabledReason,
  onViewTermNotes,
  termNotesCount = 0,
  onCheckAndImport,
  isSentToProvider,
  onSendToProvider,
  isProviderSendDisabled,
  providerSendDisabledReason,
  onViewAdmission,
  onViewInvoice,
  onViewProvider,
  onDelete,
  onSendAdminNotification,
  onForceDeleteTerm,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const notesCount = (row?.original?._termNotes ?? row?.original?.notes)?.length || 0;

  const handleOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const runAndClose = (handler) => () => {
    handler?.();
    handleClose();
  };

  return (
    <>
      <Tooltip title="More actions" arrow>
        <IconButton size="small" onClick={handleOpen}>
          <MoreVert className="text-app-text-muted" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        {onGenerateInvoice && (
          <MenuItem onClick={runAndClose(onGenerateInvoice)}>
            <ListItemIcon>
              <FaFileInvoice className="text-orange-400" />
            </ListItemIcon>
            <ListItemText>Generate Invoice</ListItemText>
          </MenuItem>
        )}
        {onSendAdminNotification && (
          <MenuItem onClick={runAndClose(onSendAdminNotification)}>
            <ListItemIcon>
              <NotificationsActiveOutlined fontSize="small" className="text-orange-400" />
            </ListItemIcon>
            <ListItemText>Send notification to admin</ListItemText>
          </MenuItem>
        )}
        {onCopyRow && (
          <MenuItem onClick={runAndClose(onCopyRow)}>
            <ListItemIcon>
              <CopyAllOutlined fontSize="small" className="text-orange-400" />
            </ListItemIcon>
            <ListItemText>Copy Row</ListItemText>
          </MenuItem>
        )}
        {onToggleNotes ? (
          <MenuItem onClick={runAndClose(onToggleNotes)}>
            <ListItemIcon>
              <NoteOutlined fontSize="small" className="text-orange-400" />
            </ListItemIcon>
            <ListItemText>View Account Notes</ListItemText>
            {notesCount > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                {notesCount}
              </span>
            )}
          </MenuItem>
        ) : (
          <Tooltip
            title={accountNotesDisabledReason || "Notes not available for this row"}
            placement="left"
            arrow
          >
            <span>
              <MenuItem disabled>
                <ListItemIcon>
                  <NoteOutlined fontSize="small" className="text-gray-300" />
                </ListItemIcon>
                <ListItemText>View Account Notes</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        )}
        {onViewTermNotes && (
          <MenuItem onClick={runAndClose(onViewTermNotes)}>
            <ListItemIcon>
              <StickyNote2Outlined fontSize="small" className="text-blue-500" />
            </ListItemIcon>
            <ListItemText>View Term Notes</ListItemText>
            {termNotesCount > 0 && (
              <span className="ml-2 bg-blue-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                {termNotesCount}
              </span>
            )}
          </MenuItem>
        )}
        {onCheckAndImport && (
          <MenuItem onClick={runAndClose(onCheckAndImport)}>
            <ListItemIcon>
              <TaskAltOutlined fontSize="small" className="text-emerald-500" />
            </ListItemIcon>
            <ListItemText>Check &amp; import</ListItemText>
          </MenuItem>
        )}
        <Tooltip
          title={
            isSentToProvider
              ? "Already sent to provider"
              : isProviderSendDisabled
              ? providerSendDisabledReason || "This installment is locked."
              : ""
          }
          placement="left"
          arrow
        >
          <span>
            <MenuItem
              onClick={runAndClose(onSendToProvider)}
              disabled={isSentToProvider || isProviderSendDisabled}
            >
              <ListItemIcon>
                <Send
                  fontSize="small"
                  className={
                    isSentToProvider || isProviderSendDisabled ? "text-gray-300" : "text-orange-400"
                  }
                />
              </ListItemIcon>
              <ListItemText>Send to Provider Accounts</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
        {onViewAdmission && (
          <MenuItem onClick={runAndClose(onViewAdmission)}>
            <ListItemIcon>
              <OpenInNew fontSize="small" className="text-blue-500" />
            </ListItemIcon>
            <ListItemText>View Admission</ListItemText>
          </MenuItem>
        )}
        {onViewProvider && (
          <MenuItem onClick={runAndClose(onViewProvider)}>
            <ListItemIcon>
              <OpenInNew fontSize="small" className="text-blue-500" />
            </ListItemIcon>
            <ListItemText>View in Provider Accounts</ListItemText>
          </MenuItem>
        )}
        {onViewInvoice && (
          <MenuItem onClick={runAndClose(onViewInvoice)}>
            <ListItemIcon>
              <OpenInNew fontSize="small" className="text-blue-500" />
            </ListItemIcon>
            <ListItemText>View Provider Invoice</ListItemText>
          </MenuItem>
        )}
        {onDelete && (
          <>
            <Divider />
            <MenuItem onClick={runAndClose(onDelete)} sx={{ color: "error.main" }}>
              <ListItemIcon>
                <DeleteOutlined fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete Course</ListItemText>
            </MenuItem>
          </>
        )}
        {onForceDeleteTerm && (
          <>
            <Divider />
            <MenuItem onClick={runAndClose(onForceDeleteTerm)} sx={{ color: "error.main" }}>
              <ListItemIcon>
                <DeleteForeverOutlined fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Force delete term (super admin)</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};
